// ITEM 7 — SHARING A POST INTO SOMEBODY'S INBOX.
//
// Her order: a search box, she types a name with or without the @, picks ONE OR MULTIPLE recipients,
// and sends. It arrives in each of their inboxes as a message linking to the post.
//
// **IT IS THE ROOM'S OWN MEMBER SEARCH, NOT A SECOND ONE.** `/api/living-room/members` is LR17-3's
// route: it normalises the @ through `memberSearchTerm`, excludes blocked pairs IN THE SQL, and hands
// back the opaque `ref` with a room first-name and nothing a surname could ride in (LR1 ruling 3). A
// second search here would be a second answer to who a term means (R28/R29).
//
// **THE SEND IS ONE POST AND NO RETRY** — her word. A recipient that refuses is NAMED rather than
// swallowed, and the whole thing is never re-sent, because a retry on a write that may already have
// landed is how one share becomes two messages in somebody's inbox.
import { useEffect, useState } from "react";
import { Search, Users, X } from "lucide-react";
import { createPortal } from "react-dom";
import { json, postJson, Avatar, type Identity } from "./LivingRoomShared";

// A recipient is a member (the identity every surface in this room draws) or a GROUP, which has an
// image and a name of its own. `kind` is what tells them apart everywhere below — never the shape.
type Recipient = Identity & { kind?: "group"; image?: string | null; members?: number };
import { useEscapeKey } from "./useEscapeKey";
import {
  SHARE_LABEL, SHARE_PICK_HINT, ROOM_READ_FAILED, memberSearchTerm, memberSearchEmpty, GROUP_RECIPIENT_LABEL,
} from "../livingRoom.js";

export function SharePicker({ postId, onClose }: { postId: string; onClose: () => void }) {
  const [q, setQ] = useState("");
  // SHARE TO GROUPS (her order, 2026-08-06): a recipient is a person OR a group, and which it is
  // travels WITH it — so the row that draws it, the chip that remembers it and the payload that
  // sends it all read one fact rather than three surfaces guessing from its shape.
  const [rows, setRows] = useState<Recipient[] | null>(null);
  const [groups, setGroups] = useState<Recipient[]>([]);
  const [picked, setPicked] = useState<Recipient[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  useEscapeKey(onClose);

  const search = async (term: string) => {
    // Idle is not empty: a box she has not typed in is asked nothing, and *no one matches* over a
    // blank box is a statement about the house made in answer to no question (LR17-3/R64).
    //
    // **HER GROUPS ARE THE EXCEPTION AND IT IS DELIBERATE.** A member has a handful of groups and
    // knows them by sight, so they are offered the moment the picker opens; people stay behind a
    // term because there are thousands of them. So a blank box still ASKS — it just asks for groups.
    const d = await json(`/api/living-room/members?q=${encodeURIComponent(term || "")}`);
    setGroups(Array.isArray(d?.groups) ? d.groups.map((g: Recipient) => ({ ...g, kind: "group" as const })) : []);
    if (!memberSearchTerm(term)) { setRows(null); if (!d?.ok) setNote(ROOM_READ_FAILED); return; }
    setRows(Array.isArray(d?.members) ? d.members : null);
    if (!d?.ok) setNote(ROOM_READ_FAILED);
  };
  // Ask once when the picker opens, so her groups are already there.
  useEffect(() => { void search(""); }, []);

  const toggle = (m: Identity) => {
    setPicked((was) => was.some((p) => String(p.ref) === String(m.ref))
      ? was.filter((p) => String(p.ref) !== String(m.ref))
      : [...was, m]);
  };

  const send = async () => {
    if (busy || !picked.length) return;
    setBusy(true);
    // The KIND rides with the ref: the server checks her membership for a group rather than trusting
    // it, so this is what she MEANT rather than what she is allowed (R141 — the server decides).
    const d = await postJson("/api/living-room/share", {
      postId,
      refs: picked.map((p) => ({ ref: String(p.ref), kind: p.kind === "group" ? "group" : "member" })),
    });
    // R39/R47 — READ THE PAYLOAD. This route answers 200 with `ok:false` when nothing went, so a
    // status code would call a refusal a success.
    if (d?.ok) {
      const missed = Array.isArray(d.refused) ? d.refused.length : 0;
      setNote(missed
        ? `Sent to ${d.shared}. ${missed} could not be reached.`
        : `Sent to ${d.shared}.`);
      if (!missed) window.setTimeout(onClose, 900);
    } else {
      setNote(d?.message || "That did not send.");
    }
    setBusy(false);
  };

  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="modal-backdrop lr-modal-backdrop" role="dialog" aria-modal="true"
      aria-label={SHARE_LABEL} onMouseDown={onClose}>
      <div className="lr-modal lr-share" onMouseDown={(e) => e.stopPropagation()}>
        <button className="lr-modal__close" type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <h2>{SHARE_LABEL}</h2>
        <div className="inbox-search">
          <Search size={15} className="inbox-search__icon" aria-hidden="true" />
          <input className="inbox-search__input" value={q} placeholder={SHARE_PICK_HINT}
            aria-label={SHARE_PICK_HINT}
            onChange={(e) => { setQ(e.target.value); search(e.target.value); }} />
        </div>

        {picked.length ? (
          <p className="lr-share__picked">
            {picked.map((m) => (
              <button key={m.ref} type="button" className="lr-share__chip" onClick={() => toggle(m)}>
                {m.kind === "group" ? m.name : `@${m.name}`} <X size={12} />
              </button>
            ))}
          </p>
        ) : null}

        {rows !== null && !rows.length ? <p className="lr-empty">{memberSearchEmpty(q)}</p> : null}
        <ul className="lr-share__rows">
          {/* HER RULE 3: groups appear WITH their image and name and are CLEARLY MARKED. They lead
              the list because they are the few she recognises; the people she searched follow. */}
          {groups.map((g) => {
            const on = picked.some((p) => String(p.ref) === String(g.ref) && p.kind === "group");
            return (
              <li key={`g${g.ref}`}>
                <button type="button" className={on ? "lr-share__row is-on" : "lr-share__row"}
                  aria-pressed={on} onClick={() => toggle(g)}>
                  {g.image
                    ? <img className="lr-share__groupimg" src={g.image} alt="" />
                    : <span className="lr-share__groupimg lr-share__groupimg--none" aria-hidden="true"><Users size={16} /></span>}
                  <span className="lr-share__row-name">{g.name}</span>
                  <span className="lr-share__kind">{GROUP_RECIPIENT_LABEL}</span>
                </button>
              </li>
            );
          })}
          {(rows || []).map((m) => {
            const on = picked.some((p) => String(p.ref) === String(m.ref));
            return (
              <li key={m.ref}>
                {/* QC-11 — **A RESULT ROW SHOWS THE FACE.** Her order: everywhere a member searches
                    for people, the picture rides beside the @name. The other two surfaces already
                    did it through `Avatar`, so this one uses the SAME component rather than a
                    second way to draw a member (LR1's law: `Avatar` is THE ONE COMPONENT THAT DRAWS
                    A MEMBER, and a member with no photo gets the house glyph from it by existing).
                    The payload already carried the photo — `searchMembers` returns `identityFrom`,
                    the same identity every other surface reads — so nothing on the server moved. */}
                <button type="button" className={on ? "lr-share__row is-on" : "lr-share__row"}
                  aria-pressed={on} onClick={() => toggle(m)}>
                  <Avatar who={m} size="sm" />
                  <span className="lr-share__row-name">@{m.name}</span>
                </button>
              </li>
            );
          })}
        </ul>

        {note ? <p className="lr-note">{note}</p> : null}
        <button type="button" className="lr-primary" disabled={busy || !picked.length} onClick={send}>
          {busy ? "Sending..." : `${SHARE_LABEL}${picked.length ? ` to ${picked.length}` : ""}`}
        </button>
      </div>
    </div>,
    document.body,
  );
}
